import React, { useEffect, useMemo, useRef, useState } from 'react';
import DataTable, { Column } from '../DataTable';
import Button from '../ui/Button';
import LoadErrorBox from '../ui/LoadErrorBox';
import SearchableSelect from '../ui/SearchableSelect';
import StatusBadge from '../ui/StatusBadge';
import { useNotifications } from '../NotificationProvider';
import { useLoad } from '../../hooks/useLoad';
import { useRole } from '../../hooks/useRole';
import { confirmShipmentReceipt, fetchShipmentReadyToReceive } from '../../services/ProcurementService';
import { fetchInventoryItems } from '../../services/InventoryService';
import { formatDate } from '../../utils/formatDate';
import { procurementErrorMessage } from '../../utils/procurementErrors';
import { newIdempotencyKey } from './ReceivingPanel';
import { fmtQty, qty } from './procurementLabels';
import {
  CREATE_STOCK, SHORTFALL_REASON_OPTIONS, buildConfirmLines, flattenLines, initialDrafts, isShort, validateDrafts,
} from './readyToReceiveLogic';
import type { Drafts, LineDraft } from './readyToReceiveLogic';
import type { ConfirmReceiptResult, ReadyLine, ShortfallReason } from '../../types/Procurement';
import '../../styles/components/ProcurementScreens.css';
import '../../styles/components/ReadyToReceive.css';

type Row = ReadyLine & { order_id: string; order_label: string };

interface Props {
  shipmentId: string;
  /** Called after a receipt was confirmed, so the page around the panel can re-read the shipment. */
  onConfirmed?: () => void;
}

/**
 * The goods on an arrived shipment, ready to be received: every linked order line still to arrive, quantity pre-filled
 * with what is outstanding. Confirming sends every order in one request, so the stock ledger, the stock level and each
 * order's received milestone move together or not at all. A short line needs a reason (a field, not prose).
 */
const ReadyToReceivePanel: React.FC<Props> = ({ shipmentId, onConfirmed }) => {
  const { showSuccess, showError } = useNotifications();
  const { isPlanner, isContractor } = useRole();
  // Contractors share the planner level but have no say over stock; the API refuses them too.
  const canConfirm = isPlanner && !isContractor;

  const ready = useLoad(() => fetchShipmentReadyToReceive(shipmentId), [shipmentId]);
  const inventory = useLoad(() => (canConfirm ? fetchInventoryItems() : Promise.resolve([])), [canConfirm]);
  const [drafts, setDrafts] = useState<Drafts>({});
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [result, setResult] = useState<{ view: ConfirmReceiptResult; labels: Record<string, string> } | null>(null);
  const key = useRef(newIdempotencyKey());

  const orders = ready.data?.orders ?? [];
  const rows: Row[] = useMemo(() => flattenLines(orders), [orders]);
  const errors = useMemo(() => validateDrafts(rows, drafts), [rows, drafts]);

  // A fresh view (first load, or after a confirm) starts from what is outstanding and gets a fresh key.
  useEffect(() => {
    if (!ready.data) return;
    setDrafts(initialDrafts(ready.data.orders));
    setShowErrors(false);
    key.current = newIdempotencyKey();
  }, [ready.data]);

  const stockOptions = useMemo(() => {
    const items = (inventory.data ?? []).map(i => ({ value: i.id, label: i.item_type ? `${i.name} (${i.item_type})` : i.name }));
    return [{ value: CREATE_STOCK, label: 'Create a new stock item' }, ...items.sort((a, b) => a.label.localeCompare(b.label))];
  }, [inventory.data]);

  const patch = (lineId: string, change: Partial<LineDraft>) => {
    setFormError(null);
    setDrafts(d => ({ ...d, [lineId]: { ...d[lineId], ...change } }));
  };

  const confirm = async () => {
    if (!canConfirm || saving || !ready.data) return;
    if (Object.keys(errors).length > 0) {
      setShowErrors(true);
      setFormError('Fix the lines marked below, then confirm.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const view = await confirmShipmentReceipt(shipmentId, { idempotency_key: key.current, lines: buildConfirmLines(rows, drafts) });
      setResult({ view, labels: Object.fromEntries(orders.map(o => [o.order_id, o.marketplace_order_id || o.seller_name || o.order_id.slice(0, 8).toUpperCase()])) });
      showSuccess('Receipt confirmed');
      await ready.reload();
      void inventory.reload();
      onConfirmed?.();
    } catch (err) {
      const message = procurementErrorMessage(err, 'The receipt could not be confirmed. Nothing was changed. Please try again.');
      setFormError(message);
      showError(message);
    } finally {
      setSaving(false);
    }
  };

  const columns: Column<Row>[] = [
    { key: 'order_label', header: 'Order' },
    { key: 'product_name', header: 'Product' },
    {
      key: 'remaining', header: 'To arrive', align: 'right',
      render: (_v, l) => <span>{fmtQty(l.remaining)}{l.unit ? ` ${l.unit}` : ''}</span>,
    },
    {
      key: 'received_now', header: 'Arrived', align: 'right',
      render: (_v, l) => {
        const d = drafts[l.line_id];
        if (!canConfirm) return <span>{fmtQty(l.suggested_quantity)}</span>;
        const err = showErrors ? errors[l.line_id]?.quantity : undefined;
        const id = `rtr-qty-${l.line_id}`;
        return (
          <div className="rtr-qty">
            <input
              id={id}
              className={`proc-qty-input${err ? ' proc-qty-input--error' : ''}`}
              type="text"
              inputMode="decimal"
              aria-label={`Arrived: ${l.product_name}`}
              aria-invalid={err ? true : undefined}
              aria-describedby={err ? `${id}-err` : undefined}
              disabled={saving}
              value={d?.quantity ?? ''}
              onChange={e => patch(l.line_id, { quantity: e.target.value })}
            />
            {err && <span id={`${id}-err`} className="form-error-text">{err}</span>}
          </div>
        );
      },
    },
    {
      key: 'reason', header: 'Why short',
      render: (_v, l) => {
        const d = drafts[l.line_id];
        if (!canConfirm || !isShort(l, d)) return <span className="rtr-muted">—</span>;
        return (
          <div className="rtr-cell-picker">
            <SearchableSelect
              value={d?.reason ?? ''}
              onChange={v => patch(l.line_id, { reason: v as ShortfallReason })}
              options={SHORTFALL_REASON_OPTIONS}
              placeholder="Choose a reason"
              ariaLabel={`Why short: ${l.product_name}`}
              disabled={saving}
              error={showErrors ? errors[l.line_id]?.reason : undefined}
            />
          </div>
        );
      },
    },
    {
      key: 'stock', header: 'Stock item',
      render: (_v, l) => {
        if (!l.tracks_stock) return <span className="rtr-muted">Not tracked in stock</span>;
        if (l.stock_item_id) return <span>{l.stock_suggestion.stock_item_name ?? 'Linked stock item'}</span>;
        if (!canConfirm) return <span className="rtr-muted">{l.stock_suggestion.stock_item_name ?? 'New stock item'}</span>;
        const d = drafts[l.line_id];
        const suggested = l.stock_suggestion;
        const options = suggested.stock_item_id && !stockOptions.some(o => o.value === suggested.stock_item_id)
          ? [...stockOptions, { value: suggested.stock_item_id, label: suggested.stock_item_name ?? 'Suggested stock item' }]
          : stockOptions;
        return (
          <div className="rtr-cell-picker">
            <SearchableSelect
              value={d?.stock ?? ''}
              onChange={v => patch(l.line_id, { stock: v })}
              options={options}
              placeholder="Choose a stock item"
              ariaLabel={`Stock item: ${l.product_name}`}
              disabled={saving}
              error={showErrors ? errors[l.line_id]?.stock : undefined}
            />
          </div>
        );
      },
    },
  ];

  return (
    <section className="proc-panel" aria-label="Ready to receive" id="ready-to-receive">
      <div className="proc-panel__head">
        <h3 className="heading-card">Ready to receive</h3>
        {canConfirm && rows.length > 0 && (
          <Button variant="primary" size="sm" loading={saving} onClick={() => void confirm()}>Confirm receipt</Button>
        )}
      </div>

      {ready.error ? (
        <LoadErrorBox what="the goods ready to receive" error={ready.error} onRetry={ready.reload} retrying={ready.loading} />
      ) : !ready.data ? (
        <p className="text-subtle" role="status">Loading the goods on this shipment...</p>
      ) : !ready.data.eligible ? (
        <p className="text-subtle">Goods can be confirmed once the shipment has arrived.</p>
      ) : rows.length === 0 ? (
        <p className="text-subtle">{result ? 'Everything on this shipment has been confirmed.' : 'Nothing is waiting to be received on this shipment.'}</p>
      ) : (
        <>
          <p className="text-subtle rtr-lead">
            {canConfirm
              ? 'The quantities below are what is still outstanding. Change any line that arrived short, say why, then confirm once for every order.'
              : 'These lines are waiting to be confirmed by a planner or an owner.'}
          </p>
          <DataTable<Row> data={rows} columns={columns} emptyMessage="Nothing to receive" compact />
          {inventory.error ? <LoadErrorBox what="the stock items" error={inventory.error} onRetry={inventory.reload} retrying={inventory.loading} /> : null}
          {formError && <p role="alert" className="form-error-text">{formError}</p>}
        </>
      )}

      {result && (
        <div className="rtr-result" role="status" aria-label="What was updated">
          <h4 className="heading-card">Updated</h4>
          <ul className="rtr-result-list">
            {result.view.orders.map(o => {
              const left = o.lines.reduce((sum, l) => sum + Math.max(qty(l.remaining), 0), 0);
              return (
                <li key={o.order_id}>
                  <strong>{result.labels[o.order_id] ?? o.order_id.slice(0, 8).toUpperCase()}</strong>
                  {o.milestones.received_at
                    ? <StatusBadge variant="success">Received {formatDate(o.milestones.received_at, '')}</StatusBadge>
                    : <StatusBadge variant="warning">Still waiting for {fmtQty(left)}</StatusBadge>}
                </li>
              );
            })}
            {result.view.stock.map(s => (
              <li key={s.item_id}>Stock: {s.name} is now {fmtQty(s.stock)}</li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

export default ReadyToReceivePanel;
